import type { Order } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { VendorShorts } from '../components/VendorShorts';
import { useAuth } from '../auth';
import { BarList, Donut, TrendChart, compact } from '../components/charts';
import { IconCustomers, IconItems, IconPlus } from '../components/icons';
import { ErrorBox, StatusBadge } from '../components/ui';
import { addDays, businessHour, formatDate, formatMoney, formatQty, todayIso } from '../format';
import { useDesktop } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { bucketNames, categoryColor } from './SalesPage';
import { SignalsHeadline } from '../components/Signals';

const STATUS_ORDER: Order['status'][] = [
  'draft', 'confirmed', 'picked', 'out_for_delivery', 'delivered', 'invoiced', 'cancelled',
];

export function HomePage() {
  const { me } = useAuth();
  const desktop = useDesktop();
  return desktop && (me?.role === 'owner' || me?.role === 'office') ? <DeskDashboard /> : <PhoneHome />;
}

/**
 * The office's dashboard (decision 72): today's numbers in a strip, the week's headline, then tables to act on:
 * past due accounts, expected orders not yet in, inventory at risk and the pending work. The sidebar is the
 * way to every screen, so there is no grid of buttons.
 */
function DeskDashboard() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const today = todayIso();
  const orders = useQuery({ queryKey: qk.orders(today), queryFn: ({ signal }) => api.orders(today, signal) });
  const dash = useQuery({ queryKey: qk.dashboard, queryFn: ({ signal }) => api.dashboard(signal) });
  const expected = useQuery({ queryKey: qk.expected(today, today), queryFn: ({ signal }) => api.expectedOrders(today, today, signal) });
  const hour = businessHour();
  const greeting = hour >= 3 && hour < 12 ? t('greetingMorning') : hour >= 12 && hour < 19 ? t('greetingAfternoon') : t('greetingEvening');
  const firstName = me?.name.trim().split(/\s+/)[0] ?? '';
  const rows = orders.data ?? [];
  const live = rows.filter((o) => o.status !== 'cancelled');
  const value = Math.round(live.reduce((s, o) => s + o.total, 0) * 100) / 100;
  const d = dash.data;
  const money = (v: number) => formatMoney(v, intlLocale);
  const waiting = (expected.data ?? []).filter((e) => !e.hasOrder);
  const pending = d
    ? [
        { n: d.orders.unpricedLines, label: t('unpricedLinesCount'), to: '/orders' },
        { n: d.inbox.texts, label: t('textsWaiting'), to: '/texts' },
        { n: d.inbox.unknownNames, label: t('unknownNamesCount'), to: '/items/inbox' },
        { n: d.inbox.unmappedDocLines, label: t('unmappedDocLines'), to: '/costs' },
        { n: d.inbox.openPurchaseOrders, label: t('openPos'), to: '/stock/receive' },
      ].filter((x) => x.n > 0)
    : [];

  return (
    <div className="desk-page dashboard">
      <header className="desk-head">
        <div>
          <h1>{t('navDashboard')}</h1>
          <p className="muted date-line">
            {firstName ? t('greetingLine').replace('{greeting}', greeting).replace('{name}', firstName) : greeting} · {formatDate(today, intlLocale, 'long')}
          </p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary btn-sm">
          {t('newInvoice')}
        </Link>
      </header>
      {me && (!me.hasPassword || me.mustChange) ? (
        <Link to="/settings/password" className="warn-band" data-testid="password-nudge">
          {me.mustChange ? t('mustChangeHint') : t('noPasswordNudge')}
        </Link>
      ) : null}
      {/* what a vendor will not bring, the moment its invoice, a call or the dock says so (decision 176) */}
      <VendorShorts />
      <dl className="totals-strip" data-testid="today-strip">
        <div>
          <dt>
            <Link to="/orders">{t('ordersToday')}</Link>
          </dt>
          <dd data-testid="orders-today">{orders.data ? live.length : '–'}</dd>
        </div>
        <div>
          <dt>{t('orderValue')}</dt>
          <dd>{orders.data ? money(value) : '–'}</dd>
        </div>
        {d ? (
          <>
            <div className={d.orders.unpricedLines > 0 ? 'warn' : undefined}>
              <dt>{t('unpricedLinesCol')}</dt>
              <dd>{d.orders.unpricedLines}</dd>
            </div>
            <div>
              <dt>{t('invoicedToday')}</dt>
              <dd>{money(d.money.invoicedToday)}</dd>
            </div>
            <div>
              <dt>{t('invoicedWeek')}</dt>
              <dd>{money(d.money.invoicedWeek)}</dd>
            </div>
            <div>
              <dt>{t('paidWeek')}</dt>
              <dd>{money(d.money.paidWeek)}</dd>
            </div>
            {d.money.openBalance !== null ? (
              <div>
                <dt>
                  <Link to="/reports/aging">{t('openBalance')}</Link>
                </dt>
                <dd>{money(d.money.openBalance)}</dd>
              </div>
            ) : null}
            {d.money.overdueBalance !== null ? (
              <div className={d.money.overdueBalance > 0 ? 'bad' : undefined}>
                <dt>{(d.money.overdueCustomers === 1 ? t('pastDueCustomer') : t('pastDueCustomers')).replace('{n}', String(d.money.overdueCustomers))}</dt>
                <dd>{money(d.money.overdueBalance)}</dd>
              </div>
            ) : null}
            {d.money.marginWeekPct !== null ? (
              <div className={d.money.marginWeekPct < 10 ? 'warn' : undefined}>
                <dt>{t('marginWeek')}</dt>
                <dd>{d.money.marginWeekPct}%</dd>
              </div>
            ) : null}
          </>
        ) : null}
      </dl>
      {dash.isError ? <ErrorBox error={dash.error} onRetry={() => void dash.refetch()} /> : null}
      {orders.isError ? <ErrorBox error={orders.error} onRetry={() => void orders.refetch()} /> : null}
      <WeatherStrip />
      <DashCharts today={today} />
      <div className="dash-grid">
        <StandingTile date={today} />
        <section className="panel" data-testid="expected-today">
          <h2>{t('expectedToday')}</h2>
          {expected.isError ? (
            <ErrorBox error={expected.error} onRetry={() => void expected.refetch()} />
          ) : !expected.data ? (
            <p className="muted" role="status">{t('loading')}</p>
          ) : waiting.length === 0 ? (
            <p className="muted">{t('nobodyWaiting')}</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('customer')}</th>
                  <th scope="col">{t('usualItemsCol')}</th>
                  <th scope="col" className="num">{t('typicalCol')}</th>
                  <th scope="col" className="num">{t('likelihoodCol')}</th>
                </tr>
              </thead>
              <tbody>
                {waiting.map((e) => (
                  <tr key={e.customerId}>
                    <td>
                      <Link to={`/orders/new?customer=${e.customerId}`}>{e.customerName}</Link>
                    </td>
                    <td className="muted">{e.items.slice(0, 3).map((i) => `${i.qty} ${i.itemName}`).join(', ')}{e.items.length > 3 ? ' …' : ''}</td>
                    <td className="num">{e.typicalTotal !== null ? money(e.typicalTotal) : ''}</td>
                    <td className="num">{Math.round(e.share * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        <CustomerTrends today={today} />
        {d && d.stockAtRisk.length > 0 ? (
          <section className="panel">
            <h2>{t('stockAtRisk')}</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('itemCol')}</th>
                  <th scope="col">{t('lotCol')}</th>
                  <th scope="col" className="num">{t('ageCol')}</th>
                  <th scope="col">{t('shelfLifeCol')}</th>
                  <th scope="col" className="num">{t('packsCol')}</th>
                </tr>
              </thead>
              <tbody>
                {d.stockAtRisk.map((r) => (
                  <tr key={r.lotId}>
                    <td>
                      <Link to={`/stock/lots?q=${encodeURIComponent(r.lotCode)}`}>{r.itemName}</Link>
                    </td>
                    <td className="muted">{r.lotCode}</td>
                    <td className="num">{r.ageDays}</td>
                    <td>{t(`shelf_${r.shelfLife}`)}</td>
                    <td className="num">{formatQty(r.onHandPacks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}
        {pending.length > 0 ? (
          <section className="panel">
            <h2>{t('waiting')}</h2>
            <p className="muted">{t('waitingHint')}</p>
            <table className="data-table">
              <tbody>
                {pending.map((x) => (
                  <tr key={x.to}>
                    <td className="num" style={{ width: 64 }}>
                      <strong>{x.n}</strong>
                    </td>
                    <td>
                      <Link to={x.to}>{x.label}</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}
      </div>
    </div>
  );
}

/** Monday of the week of a day (ISO weeks, as the sales report buckets them). */
const mondayOf = (day: string) => addDays(day, -((new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7));
/** The last four weeks, this one included: the window every 4-week panel reads (JL, 2026-10-26: "4 week comparisons"). */
export const fourWeeksFrom = (today: string) => addDays(mondayOf(today), -21);

/** The same days of the month before: the 1st through today's day of the month (or that month's last day). */
export function sameDaysLastMonth(today: string): { from: string; to: string } {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const first = new Date(Date.UTC(y, m - 2, 1));
  const last = new Date(Date.UTC(y, m - 1, 0)).getUTCDate();
  const iso = (dt: Date) => dt.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(d, last)))) };
}

/**
 * The desk dashboard's charts (decisions 84, 135): four weeks of revenue beside the four before them (no year ago:
 * the QuickBooks history before 2026 is not complete), this week and this month so far against the same days before,
 * the mix and the top customers of the last four weeks, and what is owed by age. Each opens the report behind it.
 */
function DashCharts({ today }: { today: string }) {
  const { t, intlLocale } = useLocale();
  const monday = mondayOf(today);
  const from4 = fourWeeksFrom(today);
  const from8 = addDays(monday, -49);
  const monthFrom = `${today.slice(0, 8)}01`;
  const lastMonth = sameDaysLastMonth(today);
  const opts = { staleTime: 5 * 60_000 };
  const four = useQuery({ queryKey: qk.sales(from4, today, 'week'), queryFn: ({ signal }) => api.sales({ from: from4, to: today, grain: 'week' }, signal), ...opts });
  const eight = useQuery({ queryKey: qk.sales(from8, today, 'week'), queryFn: ({ signal }) => api.sales({ from: from8, to: today, grain: 'week' }, signal), ...opts });
  const week = useQuery({ queryKey: qk.sales(monday, today, 'day'), queryFn: ({ signal }) => api.sales({ from: monday, to: today, grain: 'day' }, signal), ...opts });
  const month = useQuery({ queryKey: qk.sales(monthFrom, today, 'day'), queryFn: ({ signal }) => api.sales({ from: monthFrom, to: today, grain: 'day' }, signal), ...opts });
  const prevMonth = useQuery({ queryKey: qk.sales(lastMonth.from, lastMonth.to, 'day'), queryFn: ({ signal }) => api.sales({ from: lastMonth.from, to: lastMonth.to, grain: 'day' }, signal), ...opts });
  const aging = useQuery({ queryKey: qk.aging, queryFn: ({ signal }) => api.aging(signal), staleTime: 5 * 60_000 });
  const money = (v: number) => formatMoney(v, intlLocale);
  const short = (v: number) => compact(v, intlLocale);
  const s = four.data;
  const a = aging.data;
  const ages = ['current', 'd1to30', 'd31to60', 'd61to90', 'd90plus'] as const;
  const series = eight.data?.series ?? [];
  const now4 = series.slice(-4);
  const before4 = series.slice(-8, -4);
  const pct = (now: number, before: number) => (before > 0 ? Math.round(((now - before) / before) * 100) : null);
  const tiles: { key: string; label: string; now: number | null; before: number | null; vs: string }[] = [
    { key: 'week', label: t('thisWeekSoFar'), now: week.data?.kpis.revenue.now ?? null, before: week.data?.kpis.revenue.prior ?? null, vs: t('vsSameDaysLastWeek') },
    { key: 'month', label: t('thisMonthSoFar'), now: month.data?.kpis.revenue.now ?? null, before: prevMonth.data?.kpis.revenue.now ?? null, vs: t('vsSameDaysLastMonth') },
    { key: 'four', label: t('last4Weeks'), now: s?.kpis.revenue.now ?? null, before: s?.kpis.revenue.prior ?? null, vs: t('vsFourWeeksBefore') },
  ];
  return (
    <div className="chart-grid" data-testid="dash-charts">
      <section className="wide">
        <dl className="totals-strip" data-testid="sales-tiles">
          {tiles.map((x) => {
            const p = x.now !== null && x.before !== null ? pct(x.now, x.before) : null;
            return (
              <div key={x.key}>
                <dt>{x.label}</dt>
                <dd>
                  {x.now === null ? '…' : money(x.now)}
                  {p !== null ? (
                    <span className={p < 0 ? 'trend-down' : 'trend-up'} title={`${x.vs}: ${money(x.before!)}`}>
                      {' '}
                      {p > 0 ? '▲' : p < 0 ? '▼' : '='} {Math.abs(p)}%
                    </span>
                  ) : null}
                </dd>
                <dd className="muted tile-vs">
                  {x.vs}
                  {x.before !== null ? `: ${money(x.before)}` : ''}
                </dd>
              </div>
            );
          })}
        </dl>
      </section>
      <section className="wide">
        <div className="chart-head">
          <h2>{t('salesLast4')}</h2>
          <Link to={`/reports/sales?from=${from4}&grain=week`}>{t('salesOpen')}</Link>
        </div>
        {eight.isError ? (
          <ErrorBox error={eight.error} onRetry={() => void eight.refetch()} />
        ) : !eight.data ? (
          <p className="muted" role="status">{t('loading')}</p>
        ) : (
          <TrendChart
            label={t('salesLast4')}
            points={now4.map((b) => {
              const nm = bucketNames(b.start, b.days, 'week', intlLocale, t);
              return { key: b.start, axis: nm.axis, title: nm.title, faded: nm.partial };
            })}
            bar={{ name: t('thisPeriod'), values: now4.map((b) => b.revenue), color: 'var(--viz-1)' }}
            line={{ name: t('fourWeeksBefore'), values: now4.map((_, i) => before4[i]?.revenue ?? 0), color: 'var(--viz-2)' }}
            format={money}
            axisFormat={short}
            fadedNote={t('partialNote')}
          />
        )}
      </section>
      {s ? (
        <section>
          <h2>{t('mixLast4')}</h2>
          <Donut label={t('mixLast4')} slices={s.categories.map((c) => ({ key: c.name || '-', label: c.name || t('uncategorized'), value: c.revenue, color: categoryColor(c.sort) }))} format={short} centerLabel={t('revenue')} otherLabel={t('otherCategories')} />
        </section>
      ) : null}
      {a && a.ledgerStarted ? (
        <section>
          <div className="chart-head">
            <h2>{t('arByAge')}</h2>
            <Link to="/reports/aging">{t('agingTitle')}</Link>
          </div>
          <BarList
            label={t('arByAge')}
            rows={ages.map((k, i) => ({ key: k, label: t(`age_${k}`), value: a.totals[k], display: money(a.totals[k]), color: `var(--viz-age-${i + 1})` }))}
          />
        </section>
      ) : null}
      {s ? (
        <section>
          <h2>{t('topCustomers4')}</h2>
          <BarList
            label={t('topCustomers4')}
            empty={t('noSalesInPeriod')}
            rows={s.customers.filter((c) => c.revenue > 0).slice(0, 8).map((c) => ({ key: c.id, label: c.name, value: c.revenue, display: short(c.revenue), href: `/customers/${c.id}` }))}
          />
        </section>
      ) : null}
    </div>
  );
}

/** A customer's last four weeks against the four before: what counts as a move worth a look. */
const MOVE_PCT = 25;
const MOVE_MIN = 100;

/**
 * Who is ordering less and who is picking up (JL: the Mezquite stores are always late, so past due is no signal; the
 * trend is): the last four weeks against the four before, from the same sales report, moves of a quarter or more on
 * accounts worth at least $100.
 */
function CustomerTrends({ today }: { today: string }) {
  const { t, intlLocale } = useLocale();
  const from4 = fourWeeksFrom(today);
  const four = useQuery({ queryKey: qk.sales(from4, today, 'week'), queryFn: ({ signal }) => api.sales({ from: from4, to: today, grain: 'week' }, signal), staleTime: 5 * 60_000 });
  const money = (v: number) => formatMoney(v, intlLocale);
  const rows = four.data?.customers ?? [];
  const less = rows
    .filter((c) => c.prior >= MOVE_MIN && c.revenue < c.prior && (c.revenue === 0 || (c.pct !== null && c.pct <= -MOVE_PCT)))
    .sort((a, b) => a.change - b.change)
    .slice(0, 8);
  const more = rows
    .filter((c) => c.revenue >= MOVE_MIN && c.revenue > c.prior && (c.prior === 0 || (c.pct !== null && c.pct >= MOVE_PCT)))
    .sort((a, b) => b.change - a.change)
    .slice(0, 8);
  const list = (title: string, items: typeof rows, testId: string, down: boolean) => (
    <div className="stack-sm" data-testid={testId}>
      <h3 className={`trend-head ${down ? 'trend-down' : 'trend-up'}`}>
        {down ? '▼' : '▲'} {title}
      </h3>
      {items.length === 0 ? (
        <p className="muted">{t('noMoves')}</p>
      ) : (
        <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t('customer')}</th>
              <th scope="col" className="num">{t('last4Weeks')}</th>
              <th scope="col" className="num">{t('fourWeeksBefore')}</th>
              <th scope="col" className="num">{t('changeCol')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/customers/${c.id}`}>{c.name}</Link>
                </td>
                <td className="num">{money(c.revenue)}</td>
                <td className="num">{money(c.prior)}</td>
                <td className={`num ${down ? 'trend-down' : 'trend-up'}`}>{c.revenue === 0 ? t('stoppedOrdering') : c.prior === 0 ? t('newCustomerTrend') : `${c.pct! > 0 ? '+' : ''}${Math.round(c.pct!)}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
  return (
    <section className="panel" data-testid="customer-trends">
      <h2>{t('customerTrends')}</h2>
      <p className="muted">{t('customerTrendsHint')}</p>
      {four.isError ? (
        <ErrorBox error={four.error} onRetry={() => void four.refetch()} />
      ) : !four.data ? (
        <p className="muted" role="status">{t('loading')}</p>
      ) : (
        <div className="stack">
          {list(t('orderingLess'), less, 'ordering-less', true)}
          {list(t('pickingUp'), more, 'picking-up', false)}
        </div>
      )}
    </section>
  );
}

/** What the National Weather Service's words say the day looks like, drawn (JL: "icons that show potential rain"). */
export function weatherKind(forecast: string | null, precipPct: number | null): 'storm' | 'snow' | 'rain' | 'cloud' | 'partly' | 'wind' | 'sun' {
  const f = (forecast ?? '').toLowerCase();
  if (/thunder|t-storm|tstorm/.test(f)) return 'storm';
  if (/snow|sleet|flurr|ice|freezing/.test(f)) return 'snow';
  if (/rain|shower|drizzle/.test(f) || (precipPct ?? 0) >= 50) return 'rain';
  if (/partly|mostly sunny|mostly clear|few clouds/.test(f)) return 'partly';
  if (/cloud|overcast|fog|haze|smoke/.test(f)) return 'cloud';
  if (/wind|breez|gust/.test(f)) return 'wind';
  return 'sun';
}

function WeatherIcon({ forecast, precipPct }: { forecast: string | null; precipPct: number | null }) {
  const { t } = useLocale();
  const kind = weatherKind(forecast, precipPct);
  const sun = (
    <g stroke="var(--gold-strong)" strokeWidth="1.8" strokeLinecap="round" fill="none">
      <circle cx="12" cy="12" r="4.2" fill="var(--gold)" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line key={a} x1="12" y1="3" x2="12" y2="5.2" transform={`rotate(${a} 12 12)`} />
      ))}
    </g>
  );
  const cloud = (dx = 0, dy = 0) => <path transform={`translate(${dx} ${dy})`} d="M7 18h10a4 4 0 0 0 0-8 5.5 5.5 0 0 0-10.6 1.6A3.3 3.3 0 0 0 7 18Z" fill="var(--card)" stroke="var(--accent-ink)" strokeWidth="1.6" strokeLinejoin="round" />;
  const drops = (
    <g stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round">
      <line x1="8.5" y1="20" x2="7.5" y2="22.5" />
      <line x1="12.5" y1="20" x2="11.5" y2="22.5" />
      <line x1="16.5" y1="20" x2="15.5" y2="22.5" />
    </g>
  );
  const body =
    kind === 'sun' ? sun
    : kind === 'partly' ? (
      <>
        <g transform="translate(-3 -3) scale(0.8)">{sun}</g>
        {cloud(1, 1)}
      </>
    )
    : kind === 'cloud' ? cloud(0, -1)
    : kind === 'rain' ? (
      <>
        {cloud(0, -3)}
        {drops}
      </>
    )
    : kind === 'storm' ? (
      <>
        {cloud(0, -3)}
        <path d="M12.5 15.5 10 19.5h2.6l-1.6 3.5" fill="none" stroke="var(--gold-strong)" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      </>
    )
    : kind === 'snow' ? (
      <>
        {cloud(0, -3)}
        <g fill="var(--accent)">
          <circle cx="8.5" cy="21" r="1.1" />
          <circle cx="12.5" cy="22" r="1.1" />
          <circle cx="16.5" cy="21" r="1.1" />
        </g>
      </>
    )
    : (
      <g fill="none" stroke="var(--accent-ink)" strokeWidth="1.8" strokeLinecap="round">
        <path d="M3 9h11a3 3 0 1 0-3-3" />
        <path d="M3 13h15a3 3 0 1 1-3 3" />
        <path d="M3 17h7" />
      </g>
    );
  return (
    <svg className="wx-icon" viewBox="0 0 24 24" width="28" height="28" role="img" aria-label={t(`wx_${kind}`)}>
      <title>{forecast ?? t(`wx_${kind}`)}</title>
      {body}
    </svg>
  );
}

/** Home on a phone, and for every role but the office: today's orders, the tiles, the big buttons. */
function PhoneHome() {
  const { t, intlLocale } = useLocale();
  const { me, signOut } = useAuth();
  const today = todayIso();
  const orders = useQuery({ queryKey: qk.orders(today), queryFn: ({ signal }) => api.orders(today, signal) });

  const hour = businessHour();
  const greeting =
    hour >= 3 && hour < 12 ? t('greetingMorning') : hour >= 12 && hour < 19 ? t('greetingAfternoon') : t('greetingEvening');
  const firstName = me?.name.trim().split(/\s+/)[0] ?? '';

  const rows = orders.data ?? [];
  const counts = new Map<Order['status'], number>();
  for (const o of rows) counts.set(o.status, (counts.get(o.status) ?? 0) + 1);
  const live = rows.length - (counts.get('cancelled') ?? 0);

  return (
    <div className="stack">
      <section>
        <h1>{t('navDashboard')}</h1>
        <p className="muted date-line">
          {firstName ? t('greetingLine').replace('{greeting}', greeting).replace('{name}', firstName) : greeting} · {formatDate(today, intlLocale, 'long')}
        </p>
      </section>
      {me && me.role !== 'customer' && (!me.hasPassword || me.mustChange) ? (
        <Link to="/settings/password" className="card note" data-testid="password-nudge">
          {me.mustChange ? t('mustChangeHint') : t('noPasswordNudge')}
        </Link>
      ) : null}
      <VendorShorts />

      <Link to="/orders" className="card stat-card">
        <span className="stat-number" data-testid="orders-today">
          {orders.data ? live : '–'}
        </span>
        <span className="stat-label">{t('ordersToday')}</span>
        {rows.length ? (
          <span className="badges">
            {STATUS_ORDER.filter((s) => counts.get(s)).map((s) => (
              <span key={s} className="badge-count">
                <StatusBadge status={s} /> {counts.get(s)}
              </span>
            ))}
          </span>
        ) : null}
      </Link>
      {orders.isError && !orders.data ? <ErrorBox error={orders.error} onRetry={() => void orders.refetch()} /> : null}
      {me?.role === 'owner' || me?.role === 'office' ? (
        <>
          <SignalsTile />
          <StandingTile date={today} />
          <ExpectedTile date={today} />
          <DashboardTiles />
        </>
      ) : null}

      <div className="quick">
        {me?.role === 'readonly' ? null : (
        <Link to="/orders/new" className="btn btn-primary btn-lg btn-block">
          <IconPlus /> {t('newOrder')}
        </Link>
        )}
        {me?.role === 'owner' || me?.role === 'office' ? (
          // a call or a text pasted in becomes the invoice to check (decision 142)
          <Link to="/paste" className="btn btn-secondary btn-lg btn-block">
            {t('pasteTitle')}
          </Link>
        ) : null}
        <div className="quick-grid">
          <Link to="/items" className="btn btn-secondary btn-lg">
            <IconItems /> {t('items')}
          </Link>
          <Link to="/customers" className="btn btn-secondary btn-lg">
            <IconCustomers /> {t('customers')}
          </Link>
        </div>
        {me && me.role !== 'readonly' && me.role !== 'customer' ? (
          // whoever hears it at the door, the dock or on the phone records it (decision 97)
          <Link to="/lost-sales" className="btn btn-secondary btn-lg btn-block">
            {t('lostSaleRecord')}
          </Link>
        ) : null}
        {me?.role === 'driver' || me?.role === 'owner' || me?.role === 'office' ? (
          <Link to="/route" className={me?.role === 'driver' ? 'btn btn-primary btn-lg btn-block' : 'btn btn-secondary btn-lg btn-block'}>
            {t('routesTitle')}
          </Link>
        ) : null}
        {me?.role === 'owner' || me?.role === 'office' || me?.role === 'sales' ? (
          <div className="quick-grid">
            <Link to="/customers?tab=followups" className="btn btn-secondary btn-lg">
              {t('customersTabFollowups')}
            </Link>
            <Link to="/texts" className="btn btn-secondary btn-lg">
              {t('textsTitle')}
            </Link>
          </div>
        ) : null}
        {me?.role === 'owner' || me?.role === 'office' ? (
          <>
            <div className="quick-grid">
              <Link to="/reports/profit" className="btn btn-secondary btn-lg">
                {t('profitTitle')}
              </Link>
              <Link to="/reports/exceptions" className="btn btn-secondary btn-lg">
                {t('exceptionsTitle')}
              </Link>
            </div>
            <div className="quick-grid">
              <Link to="/reports/aging" className="btn btn-secondary btn-lg">
                {t('agingTitle')}
              </Link>
            </div>
            <Link to="/money/payables" className="btn btn-secondary btn-lg btn-block">
              {t('payablesTitle')}
            </Link>
          </>
        ) : null}
        {me?.role === 'owner' || me?.role === 'office' ? (
          <>
            <Link to="/order-day" className="btn btn-secondary btn-lg btn-block">
              {t('navOrderDay')}
            </Link>
            <Link to="/prices" className="btn btn-secondary btn-lg btn-block">
              {t('pricesTitle')}
            </Link>
            <Link to="/costs/bills" className="btn btn-secondary btn-lg btn-block">
              {t('vendorBills')}
            </Link>
            <Link to="/costs/price-lists" className="btn btn-secondary btn-lg btn-block">
              {t('vendorPriceLists')}
            </Link>
          </>
        ) : null}
      </div>

      {me?.role === 'owner' ? (
        <>
          <Link to="/settings/users" className="btn btn-ghost btn-block">
            {t('usersTitle')}
          </Link>
          <Link to="/settings/sites" className="btn btn-ghost btn-block">
            {t('sitesTitle')}
          </Link>
          <Link to="/settings/connector" className="btn btn-ghost btn-block">
            {t('connectorTitle')}
          </Link>
          <Link to="/settings/system" className="btn btn-ghost btn-block">
            {t('systemTitle')}
          </Link>
        </>
      ) : null}
      {me && me.role !== 'customer' ? (
        <Link to="/settings/password" className="btn btn-ghost btn-block phone-only">
          {t('changePassword')}
        </Link>
      ) : null}
      {/* on a desk the sidebar carries these */}
      <button type="button" className="btn btn-ghost btn-block phone-only" onClick={() => void signOut()}>
        {t('signOut')}
      </button>
    </div>
  );
}

/** Tomorrow's (or today's) standing orders: how many fall on the day, how many are drafted, one tap to draft the rest. */
function StandingTile({ date }: { date: string }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const target = businessHour() >= 12 ? addDays(date, 1) : date;
  const due = useQuery({ queryKey: qk.standingDue(target), queryFn: ({ signal }) => api.standingDue(target, signal) });
  const run = useMutation({
    mutationFn: () => api.runStanding(target),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.standingDue(target) });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
  if (!due.data || due.data.due === 0) return null;
  const left = due.data.due - due.data.drafted;
  return (
    <section className="card stat-card">
      <span className="stat-label">
        {t('standingDue')} {formatDate(target, intlLocale, 'day')}
      </span>
      <span className="stat-number">
        {due.data.drafted} / {due.data.due}
      </span>
      <span className="row-sub">{t('drafted')}</span>
      {left > 0 ? (
        <button type="button" className="btn btn-primary" onClick={() => run.mutate()} disabled={run.isPending}>
          {run.isPending ? t('sending') : `${t('draftStanding')} (${left})`}
        </button>
      ) : null}
      {run.data?.heldCustomers?.length ? (
        <p className="warn-band" role="status" data-testid="standing-held">
          {t('standingHeld').replace('{names}', run.data.heldCustomers.join(', '))}
        </p>
      ) : null}
      {run.error ? <ErrorBox error={run.error} /> : null}
    </section>
  );
}

/** Money, slow payers, stock at risk and what waits in the inboxes: the owner's glance. */
/** How are things: the week's numbers with direction (decision 55); the page has the movers. */
function SignalsTile() {
  const { t, intlLocale } = useLocale();
  const q = useQuery({ queryKey: qk.signals, queryFn: ({ signal }) => api.signals(signal), staleTime: 5 * 60_000 });
  if (!q.data) return null;
  const s = q.data;
  return (
    <section className="card stack-sm" data-testid="signals">
      <h2>{t('signalsTitle')}</h2>
      <SignalsHeadline s={s} intlLocale={intlLocale} />
      <Link to="/reports/sales" className="btn btn-secondary btn-block">
        {t('salesOpen')}
      </Link>
    </section>
  );
}

/** Customers whose pattern says they order today and who have not ordered yet: the office calls them before the truck leaves (decision 52). */
function ExpectedTile({ date }: { date: string }) {
  const { t, intlLocale } = useLocale();
  const expected = useQuery({ queryKey: qk.expected(date, date), queryFn: ({ signal }) => api.expectedOrders(date, date, signal) });
  if (!expected.data || expected.data.length === 0) return null;
  const waiting = expected.data.filter((e) => !e.hasOrder);
  return (
    <section className="card stack-sm" data-testid="expected-today">
      <h2>{t('expectedToday')}</h2>
      {waiting.length === 0 ? (
        <p className="muted">{t('nobodyWaiting')}</p>
      ) : (
        <ul className="list">
          {waiting.map((e) => (
            <li key={e.customerId}>
              <Link to={`/orders/new?customer=${e.customerId}`} className="row">
                <span className="row-main">
                  <span className="row-title">{e.customerName}</span>
                  <span className="row-sub">{e.items.slice(0, 4).map((i) => `${i.qty} ${i.itemName}`).join(', ')}{e.items.length > 4 ? ' …' : ''}</span>
                </span>
                <span className="row-end">
                  {e.typicalTotal !== null ? <span className="row-end-value">{formatMoney(e.typicalTotal, intlLocale)}</span> : null}
                  <span className="row-end-label">{Math.round(e.share * 100)}%</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DashboardTiles() {
  const { t, intlLocale } = useLocale();
  const dash = useQuery({ queryKey: qk.dashboard, queryFn: ({ signal }) => api.dashboard(signal) });
  if (!dash.data) return null;
  const d = dash.data;
  return (
    <>
      <section className="card stack-sm">
        <h2>{t('money')}</h2>
        <dl className="facts plain">
          <div className="fact">
            <dt>{t('invoicedToday')}</dt>
            <dd className="money">{formatMoney(d.money.invoicedToday, intlLocale)}</dd>
          </div>
          <div className="fact">
            <dt>{t('invoicedWeek')}</dt>
            <dd>{formatMoney(d.money.invoicedWeek, intlLocale)}</dd>
          </div>
          <div className="fact">
            <dt>{t('paidWeek')}</dt>
            <dd>{formatMoney(d.money.paidWeek, intlLocale)}</dd>
          </div>
          {d.money.openBalance !== null ? (
            <div className="fact">
              <dt>{t('openBalance')}</dt>
              <dd>
                {formatMoney(d.money.openBalance, intlLocale)}
                {d.money.overdueBalance ? <span className="owed"> · {formatMoney(d.money.overdueBalance, intlLocale)} {t('overdue')}</span> : null}
              </dd>
            </div>
          ) : null}
          {d.money.marginWeekPct !== null ? (
            <div className="fact">
              <dt>{t('marginWeek')}</dt>
              <dd>{d.money.marginWeekPct}%</dd>
            </div>
          ) : null}
        </dl>
      </section>
      <CustomerTrends today={todayIso()} />
      {d.stockAtRisk.length > 0 ? (
        <section className="card stack-sm">
          <h2>{t('stockAtRisk')}</h2>
          <ul className="list">
            {d.stockAtRisk.map((r) => (
              <li key={r.lotId}>
                <Link to={`/stock/lots?q=${encodeURIComponent(r.lotCode)}`} className="row">
                  <span className="row-main">
                    <span className="row-title">{r.itemName}</span>
                    <span className="row-sub">
                      <span className="label">{r.lotCode}</span> {r.ageDays} {t('ageDays')} · {t(`shelf_${r.shelfLife}`)}
                    </span>
                  </span>
                  <span className="row-end-value">{formatQty(r.onHandPacks)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {d.inbox.unknownNames + d.inbox.unmappedDocLines + d.inbox.openPurchaseOrders + d.orders.unpricedLines + d.inbox.texts > 0 ? (
        <section className="card stack-sm">
          <h2>{t('waiting')}</h2>
          <p className="muted">{t('waitingHint')}</p>
          <ul className="list">
            {d.orders.unpricedLines > 0 ? (
              <li>
                <Link to="/orders" className="row">
                  <span className="row-main">{d.orders.unpricedLines} {t('unpricedLinesCount')}</span>
                </Link>
              </li>
            ) : null}
            {d.inbox.texts > 0 ? (
              <li>
                <Link to="/texts" className="row">
                  <span className="row-main">{d.inbox.texts} {t('textsWaiting')}</span>
                </Link>
              </li>
            ) : null}
            {d.inbox.unknownNames > 0 ? (
              <li>
                <Link to="/items/inbox" className="row">
                  <span className="row-main">{d.inbox.unknownNames} {t('unknownNamesCount')}</span>
                </Link>
              </li>
            ) : null}
            {d.inbox.unmappedDocLines > 0 ? (
              <li>
                <Link to="/costs" className="row">
                  <span className="row-main">{d.inbox.unmappedDocLines} {t('unmappedDocLines')}</span>
                </Link>
              </li>
            ) : null}
            {d.inbox.openPurchaseOrders > 0 ? (
              <li>
                <Link to="/stock/receive" className="row">
                  <span className="row-main">{d.inbox.openPurchaseOrders} {t('openPos')}</span>
                </Link>
              </li>
            ) : null}
          </ul>
        </section>
      ) : null}
    </>
  );
}

/** The week's weather at the hub (decision 113): heat and rain move what restaurants buy; nothing shows until the job ran. */
function WeatherStrip() {
  const { t, intlLocale } = useLocale();
  const query = useQuery({ queryKey: qk.weather, queryFn: ({ signal }) => api.weather(7, signal), staleTime: 60 * 60_000 });
  const days = query.data ?? [];
  if (days.length === 0) return null;
  return (
    <>
    <p className="muted">{t('weatherTitle')}</p>
    <dl className="totals-strip" data-testid="weather" aria-label={t('weatherTitle')}>
      {days.map((d) => (
        <div key={d.day} className={(d.highF ?? 0) >= 95 || (d.precipPct ?? 0) >= 60 ? 'warn' : undefined} title={d.forecast ?? undefined}>
          <dt>{formatDate(d.day, intlLocale, 'day')}</dt>
          <dd className="wx-day">
            <WeatherIcon forecast={d.forecast} precipPct={d.precipPct} />
            {d.highF === null ? '–' : `${d.highF}°`}
            {d.lowF !== null ? <span className="muted"> / {d.lowF}°</span> : null}
            {d.precipPct ? <span className={d.precipPct >= 40 ? 'wx-rain' : 'muted'}> · {d.precipPct}%</span> : null}
          </dd>
        </div>
      ))}
    </dl>
    </>
  );
}
